/* Emergency response-SLA ladder. Clock starts when the emergency is logged and stops when the first crew member
   arrives on site (arrived_at) or the job is completed/cancelled. Tiers by share of the SLA window elapsed:
   normal < 50% · amber 50–75% · red 75–100% · breached > 100%; met / late once the crew has arrived. */

export const TIER_UI = {
  normal:   { edge: "border-l-transparent", label: "", box: "border-border", bar: "bg-emerald-500", text: "text-foreground", beacon: null, badge: "" },
  amber:    { edge: "border-l-amber-500", label: "Half way through SLA", box: "border-amber-400 bg-amber-50/70 dark:border-amber-600 dark:bg-amber-950/25", bar: "bg-amber-500", text: "text-amber-700 dark:text-amber-400", beacon: "amber", badge: "bg-amber-500 text-white" },
  red:      { edge: "border-l-red-500", label: "¾ through SLA", box: "border-red-400 bg-red-50/70 dark:border-red-700 dark:bg-red-950/25", bar: "bg-red-500", text: "text-red-600 dark:text-red-400", beacon: "red", badge: "bg-red-500 text-white" },
  breached: { edge: "border-l-red-600", label: "SLA BREACHED", box: "border-red-600 bg-red-100/90 dark:border-red-500 dark:bg-red-950/50 ring-1 ring-red-500/40", bar: "bg-red-600", text: "text-red-700 dark:text-red-300", beacon: "red", badge: "bg-red-600 text-white animate-pulse" },
  met:      { edge: "border-l-emerald-400", label: "Responded within SLA", box: "border-emerald-300 bg-emerald-50/60 dark:border-emerald-800 dark:bg-emerald-950/20", bar: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-400", beacon: null, badge: "bg-emerald-600 text-white" },
  late:     { edge: "border-l-red-400", label: "Responded after SLA", box: "border-red-300 bg-red-50/40 dark:border-red-800 dark:bg-red-950/20", bar: "bg-red-400", text: "text-red-600 dark:text-red-400", beacon: null, badge: "bg-red-500/90 text-white" },
  done:     { edge: "border-l-transparent", label: "", box: "border-border", bar: "bg-muted-foreground/40", text: "text-muted-foreground", beacon: null, badge: "" },
};

const ms = (iso) => (iso ? new Date(iso).getTime() : null);

export function emergencySla(job, now = Date.now()) {
  const hours = job.response_hours || 8;
  const windowMs = hours * 3600 * 1000;
  const created = ms(job.created_at) ?? now;
  const deadline = created + windowMs;
  const done = ["completed", "cancelled"].includes(job.status);
  const arrived = ms(job.arrived_at);
  const stop = arrived ?? (done ? (ms(job.completed_at) ?? now) : now);
  const elapsed = Math.max(0, stop - created);
  const remaining = deadline - stop;
  const pct = Math.min(100, (elapsed / windowMs) * 100);
  let tier = "normal";
  if (arrived) tier = arrived <= deadline ? "met" : "late";
  else if (done) tier = "done";
  else if (remaining <= 0) tier = "breached";
  else if (pct >= 75) tier = "red";
  else if (pct >= 50) tier = "amber";
  return { tier, ui: TIER_UI[tier], pct, elapsed, remaining, deadline, arrived, hours, live: !done && !arrived, escalated: tier === "amber" || tier === "red" || tier === "breached" };
}

/** "4h", "1.5h" or "30m" for fractional SLAs (e-mails can ask for "within 30 minutes"). */
export function slaHours(h) {
  const n = Number(h || 0);
  if (!n) return "—";
  return n < 1 ? `${Math.round(n * 60)}m` : `${Number.isInteger(n) ? n : n.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")}h`;
}

export function fmtDuration(msTotal) {
  const total = Math.max(0, Math.floor(Math.abs(msTotal) / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
}
